import React from 'react';
import { TagProps, TagTone } from '../../../types/DesignSystem/Tag';
import { Cross } from '../Iconography/Icons';
import { FOCUS_RING } from '../focusRing';
import { TONE_GLYPH } from '../toneGlyph';
import { withClassName } from '../cn';

/**
 * Tag (§ 52) — the removable value chip, extracted from the shape
 * `AttributeMultiSelectChips` hand-rolls in Products (down to the hardcoded
 * `#DEEBFF`/`#0747A6` — the RETIRED Atlassian selection colours, still shipping
 * there until it adopts this).
 *
 * A tag is a SELECTED value, so it wears the § 49 selection grammar: `blue-100`
 * ground under `accent` ink (15.82:1). The remove glyph turns `red-error-900` on
 * hover — removal is destructive and is named as one — and the button is a 24px
 * target (SC 2.5.8) inside a 28px chip, matching `Badge`'s scale so the two chip
 * shapes sit on one row without wobble.
 *
 * No `onRemove`, no button: a read-only tag is a plain chip, not a disabled one.
 *
 * § 66 added `variant="meta"` — the quiet chip for values that describe rather
 * than values the user selected. See the type file for the grammar split.
 *
 * ## § 67 — `tone`, for a chip whose value carries a STATE (2026-08-30)
 *
 * `variant` answers "was this chosen, or is it describing". Neither answer fits a
 * chip reporting that a value was **added, changed or removed**, and that gap is why
 * Catalog Management's Data Review shipped **six** private chip renderers — four in
 * `ExpandedRowContent` and `AttributeValuesModal`, plus the same shape again for the
 * row-level markers — each with its own hardcoded `bg-*-100 / border-*-500` pairs.
 *
 * `tone` reuses `Status`'s exact ground/ink pairs, so the two chip families cannot
 * drift and the measured ratios carry over: neutral 5.18, info 7.85, success 7.54,
 * warning 6.86, danger 5.58 — **each against its own tint**. A contrast figure without
 * its ground named is not a contrast figure; that distinction is what caught `Status`'s
 * own `warning` pair at 4.36:1 in § 36.
 *
 * Resolution order matches `Status`: an explicit `tone` wins over `variant`, so the
 * two coexist during a migration rather than fighting.
 *
 * **`showToneGlyph` is not decoration.** Where tone is the only difference between two
 * chips — an "added" value next to an "updated" one, same shape, same size, different
 * hue — colour is the sole carrier of meaning and SC 1.4.1 is not met. The glyph comes
 * from the shared `TONE_GLYPH` map, so a warning chip here and a warning toast wear the
 * same triangle.
 *
 * **The ink is not `Status`'s ink by accident.** Note what is NOT shared: the *hover*
 * ink on the remove button stays `tone-danger-glyph` regardless of tone. Removal is
 * destructive whatever the chip reports, and a "removed" chip whose × turns a slightly
 * different red on hover reads as a rendering bug rather than a state.
 */

/**
 * Tone → ground + ink. Identical pairs to `Status`'s `toneStyles`, deliberately.
 *
 * These are role tokens, so they follow the dark ladder in `tokens.css` without a
 * second map here — which is the whole reason the module's hardcoded
 * `bg-yellow-warning-100 / border-yellow-warning-500` chips could never go dark.
 */
const toneStyles: Record<TagTone, string> = {
  neutral: 'tw-bg-tone-neutral-tint tw-text-tone-neutral-ink',
  info: 'tw-bg-tone-info-tint tw-text-tone-info-ink',
  success: 'tw-bg-tone-success-tint tw-text-tone-success-ink',
  warning: 'tw-bg-tone-warning-tint tw-text-tone-warning-ink',
  danger: 'tw-bg-tone-danger-tint tw-text-tone-danger-ink',
};

const Tag = React.forwardRef<HTMLSpanElement, TagProps>(function Tag({
  label,
  onRemove,
  removeLabel,
  removeIcon,
  variant = 'selected',
  outlined = false,
  stateLabel,
  tone,
  showToneGlyph = false,
  disabled = false,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  /* `disabled` outranks everything: a chip nobody can act on must not keep shouting a
     state in full-strength colour. Then `tone`, then `variant` — same precedence
     `Status` uses, so one rule covers both chips. */
  const colourPair = disabled
    ? 'tw-bg-surface-sunken tw-text-ink-disabled'
    : tone
      ? toneStyles[tone]
      : variant === 'meta'
        /* The quiet pair (§ 66): this chip DESCRIBES, it was not chosen. */
        ? 'tw-bg-surface-sunken tw-text-ink-secondary'
        : 'tw-bg-accent-tint tw-text-accent';

  const ToneGlyph = tone && showToneGlyph ? TONE_GLYPH[tone] : null;

  return (
    <span
      /* rest first — see the rule in Button.tsx. */
      {...rest}
      ref={ref}
      data-testid={dataTestId}
      /* `data-tone` mirrors `Status`, so a spec can assert the state without asserting
         a Tailwind class — the assertion that survives a palette change. */
      data-tone={tone}
      /* `outlined` (§ 203): a 4px-radius rectangle bordered in the chip's own ink —
         `border-current`, so tone/variant/disabled inks border themselves with no
         second colour table. The default stays the borderless pill.
         `max-w-full` (AB#14850): the label caps at 160px, but the pill adds padding on
         top, so in a cell narrower than ~176px the pill overflowed into the next column.
         Capping the pill at its container lets the truncating label shrink instead. */
      className={withClassName(`tw-inline-flex tw-items-center tw-gap-1 tw-min-h-7 tw-px-2 tw-max-w-full ${
        outlined ? 'tw-rounded-ctl tw-border tw-border-solid tw-border-current' : 'tw-rounded-full'
      } tw-text-caption-metadata ${colourPair}`, className)}
    >
      {ToneGlyph && (
        /* Decorative: the state it stands for is in the chip's own text and, where it
           is not, in the column heading beside it. Announcing "warning" before every
           value in a diff column would be noise, not information. */
        <ToneGlyph size={12} aria-hidden="true" className="tw-shrink-0" />
      )}
      <span className="tw-max-w-[160px] tw-truncate" title={label}>
        {label}
      </span>
      {/*
        The tone's meaning, for anyone the colour does not reach. `showToneGlyph`
        cannot do this job — its glyph is `aria-hidden`, deliberately, so it has
        always been a sighted-only signal. Rendered as a separate node rather than
        appended to `label` so `title` and the remove button's accessible name stay
        the value the user recognises, not "description new".
      */}
      {stateLabel && <span className="tw-sr-only">{`, ${stateLabel}`}</span>}
      {onRemove && (
        <button
          type="button"
          disabled={disabled}
          aria-label={removeLabel ?? `Remove ${label}`}
          onClick={onRemove}
          /* -mr-1 pulls the 24px target into the chip's right padding so the
             visual chip stays compact while the hit area does not shrink. */
          className={`tw-flex tw-items-center tw-justify-center tw-h-6 tw-w-6 -tw-mr-1 tw-rounded-full ${
            disabled ? 'tw-text-ink-disabled tw-cursor-not-allowed' : 'motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift hover:tw-text-tone-danger-glyph'
          } ${FOCUS_RING}`}
          data-testid={dataTestId ? `${dataTestId}-remove` : undefined}
        >
          {removeIcon ?? <Cross size={12} aria-hidden="true" />}
        </button>
      )}
    </span>
  );
});

export default Tag;
